import React, { useState, useMemo, useEffect, useRef, useCallback } from "react";
import { Row, Col, Table, Modal, ModalHeader, ModalBody } from "reactstrap";
import {
  useTable,
  useSortBy,
  usePagination,
  useGlobalFilter,
} from "react-table";
import NewPagination from "components/Common/Newpagination";
import { customGlobalFilter } from "utils/common-helpers";
import { fetchNodeData, FetchNodePostData } from "utils/common-fetchData";
import { DatePicker } from "antd";
import dayjs from "dayjs";
import Loader from "pages/Separate/Loader";
import configure from "configure";
import Swal from "sweetalert2";

const { RangePicker } = DatePicker;

const API_MAP = {
  lead:     { removeWarning: "remove_lead_note_warning",     verify: "lead_note_verification"     },
  customer: { removeWarning: "remove_customer_note_warning", verify: "customer_note_verification" },
  project:  { removeWarning: "remove_project_note_warning",  verify: "project_note_verification"  },
};

function TableContainer({
  columns,
  data,
  personFilter,
  setPersonFilter,
  personOptions,
  typeFilter,
  setTypeFilter,
}) {
  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    page,
    state,
    previousPage,
    nextPage,
    canPreviousPage,
    canNextPage,
    gotoPage,
    setPageSize,
    pageOptions,
    setGlobalFilter,
    prepareRow,
  } = useTable(
    {
      columns,
      data,
      globalFilter: customGlobalFilter,
      autoResetPage: false,
      initialState: {
        sortBy: [{ id: "created_at", desc: true }],
      },
    },
    useGlobalFilter,
    useSortBy,
    usePagination,
  );

  const { pageIndex, pageSize, globalFilter } = state;

  useEffect(() => {
    setPageSize(10);
  }, []);

  return (
    <div>
      <Row>
        <Col xl="12">
          <div className="card-body">
            <div className="d-flex justify-content-between align-items-center mb-3 flex-wrap gap-2">
              <div className="d-flex align-items-center gap-2">
                <select
                  className="border-radius form-select"
                  style={{ width: "110px" }}
                  value={pageSize}
                  onChange={(e) => setPageSize(e.target.value)}
                >
                  {[10, 25, 50, 100].map((size) => (
                    <option key={size} value={size}>
                      Show {size}
                    </option>
                  ))}
                </select>
                <select
                  className="border-radius form-select"
                  style={{ width: "140px" }}
                  value={typeFilter}
                  onChange={(e) => setTypeFilter(e.target.value)}
                >
                  <option value="">All Types</option>
                  <option value="lead">🎯 Lead</option>
                  <option value="customer">👤 Customer</option>
                  <option value="project">📁 Project</option>
                </select>
                <select
                  className="border-radius form-select"
                  style={{ width: "160px" }}
                  value={personFilter}
                  onChange={(e) => setPersonFilter(e.target.value)}
                >
                  <option value="">All Persons</option>
                  {personOptions.map((person, idx) => (
                    <option key={idx} value={person}>
                      {person}
                    </option>
                  ))}
                </select>
              </div>
              <div className="cusrtom_search_box" style={{ width: "220px" }}>
                <div className="search-box d-inline-block w-100">
                  <div className="d-flex">
                    <div className="position-relative w-100">
                      <label
                        htmlFor="search-bar-0"
                        className="search-label w-100 mb-0"
                      >
                        <span id="search-bar-0-label" className="sr-only">
                          Search this table
                        </span>
                        <input
                          onChange={(e) => setGlobalFilter(e.target.value)}
                          id="search-bar-0"
                          type="text"
                          className="border-radius form-control rounded h-100 w-100"
                          placeholder="Search..."
                          value={globalFilter || ""}
                        />
                      </label>
                      <i className="bx bx-search-alt search-icon"></i>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="position-relative table-responsive">
              <Table className="table table-bordered" {...getTableProps()}>
                <thead className="fixed_view_payment_table">
                  {headerGroups.map((headerGroup, i) => (
                    <tr key={i} {...headerGroup.getHeaderGroupProps()}>
                      {headerGroup.headers.map((column, index) => (
                        <th
                          key={index}
                          className={`t-col-${index + 1} ${column.className}`}
                          style={column.colStyle}
                          {...column.getHeaderProps(
                            column.getSortByToggleProps(),
                          )}
                        >
                          <div className="d-flex align-items-center">
                            <span className="flex-grow-1 text-center header-text">
                              {column.render("Header")}
                            </span>
                            {column.isShortIcon && (
                              <span
                                onClick={() =>
                                  column.getSortByToggleProps().onClick()
                                }
                                className="d-flex align-items-center ms-2"
                              >
                                <i
                                  title="Ascending"
                                  className={`bi bi-arrow-up info-icon ${
                                    column.isSorted && !column.isSortedDesc
                                      ? "text-dark"
                                      : ""
                                  }`}
                                ></i>
                                <i
                                  title="Descending"
                                  className={`bi bi-arrow-down info-icon ${
                                    column.isSorted && column.isSortedDesc
                                      ? "text-dark"
                                      : ""
                                  }`}
                                ></i>
                              </span>
                            )}
                          </div>
                        </th>
                      ))}
                    </tr>
                  ))}
                </thead>
                <tbody className="" {...getTableBodyProps()}>
                  {page.length === 0 ? (
                    <tr>
                      <td
                        colSpan={columns.length}
                        className="text-center py-4 text-muted"
                      >
                        No notes found for the selected date range.
                      </td>
                    </tr>
                  ) : (
                    page.map((row, i) => {
                      prepareRow(row);
                      const score = row.original.meanScore;
                      let rowBg = "";
                      if (score < 30) rowBg = "table-danger";
                      else if (score < 50) rowBg = "table-warning";

                      return (
                        <tr
                          className={`row-hover ${rowBg}`}
                          {...row.getRowProps()}
                          key={i}
                        >
                          {row.cells.map((cell, index) => (
                            <td
                              key={index}
                              className={`t-col-${index + 1} ${
                                cell.column.className
                              }`}
                              style={cell.column.colStyle}
                              {...cell.getCellProps()}
                            >
                              {cell.render("Cell")}
                            </td>
                          ))}
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </Table>
            </div>

            <Row className="justify-content-md-end justify-content-center align-items-center">
              <NewPagination
                gotoPage={gotoPage}
                canPreviousPage={canPreviousPage}
                previousPage={previousPage}
                canNextPage={canNextPage}
                pageIndex={pageIndex}
                pageOptions={pageOptions}
                pageSize={pageSize}
                page={page}
                data={data}
                nextPage={nextPage}
              />
            </Row>
          </div>
        </Col>
      </Row>
    </div>
  );
}

const NotesCell = ({ html, onView }) => {
  const contentRef = useRef(null);
  const [isOverflowing, setIsOverflowing] = useState(false);

  useEffect(() => {
    if (contentRef.current) {
      setIsOverflowing(contentRef.current.scrollHeight > contentRef.current.clientHeight);
    }
  }, [html]);

  return (
    <div style={{ position: "relative" }}>
      <div
        ref={contentRef}
        className="notes-html-content"
        style={{
          textAlign: "left",
          whiteSpace: "normal",
          wordBreak: "break-word",
          lineHeight: "1.5",
          maxHeight: "7.5em",
          overflow: "hidden",
          paddingRight: isOverflowing ? "28px" : "0",
        }}
        dangerouslySetInnerHTML={{ __html: html }}
      />
      {isOverflowing && (
        <button
          className="btn btn-sm btn-purple"
          title="View Full Note"
          style={{ position: "absolute", top: 0, right: 0, padding: "2px 6px", fontSize: "11px" }}
          onClick={() => onView(html)}
        >
          <i className="bi bi-eye-fill"></i>
        </button>
      )}
    </div>
  );
};

const ScoreFlag = ({ score }) => {
  if (score < 30) {
    return (
      <span title="Critical" className="badge bg-danger" style={{ fontSize: "11px", padding: "4px 8px" }}>
        <i className="bi bi-flag-fill"></i>
      </span>
    );
  }
  if (score < 50) {
    return (
      <span title="Low" className="badge bg-warning text-dark" style={{ fontSize: "11px", padding: "4px 8px" }}>
        <i className="bi bi-flag"></i>
      </span>
    );
  }
  return (
    <span title="Good" className="badge bg-success" style={{ fontSize: "11px", padding: "4px 8px" }}>
      <i className="bi bi-check-circle-fill"></i>
    </span>
  );
};

const Notesanalysis = () => {
  const today = dayjs().format("YYYY-MM-DD");
  const [fromDate, setFromDate] = useState(dayjs().subtract(2, "day").format("YYYY-MM-DD"));
  const [toDate, setToDate] = useState(today);
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [personFilter, setPersonFilter] = useState("");
  const [typeFilter, setTypeFilter] = useState("");
  const [staffList, setStaffList] = useState([]);
  const [viewNote, setViewNote] = useState(null);
  const refreshRef = useRef(null);

  useEffect(() => {
    getStaff();
  }, []);

  useEffect(() => {
    getLeadNotes(fromDate, toDate);
  }, [fromDate, toDate]);

  async function getStaff() {
    const apiData = await fetchNodeData(null, "staff");
    if (apiData?.data) {
      setStaffList(apiData.data.filter((s) => s.login_access == 1));
    }
  }

  async function getLeadNotes(from, to) {
    const apiData = await fetchNodeData(
      setLoading,
      `lead_notes_by_date?from_date=${from}&to_date=${to}`,
    );
    if (apiData?.data) {
      setData(apiData.data);
    }
  }

  refreshRef.current = () => getLeadNotes(fromDate, toDate);

  const personOptions = useMemo(
    () => staffList.map((s) => s.alias_name).filter(Boolean),
    [staffList],
  );

  const filteredData = useMemo(() => {
    let result = data;
    if (personFilter) result = result.filter((item) => item.added_by === personFilter);
    if (typeFilter) result = result.filter((item) => item.type === typeFilter);
    return result;
  }, [data, personFilter, typeFilter]);

  const verificationStats = useMemo(() => {
    const total    = filteredData.length;
    const verified = filteredData.filter((item) => item.verification_status == 1).length;
    const pending  = total - verified;
    return { total, verified, pending };
  }, [filteredData]);

  const onRemoveWarning = useCallback(async (row) => {
    const confirm = await Swal.fire({
      title: "Reset AI Warning?",
      text: "This will mark the AI warning as resolved for this note.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Yes, Reset",
      cancelButtonText: "Cancel",
      confirmButtonColor: "#5c0385",
    });
    if (!confirm.isConfirmed) return;
    const { id: note_id, type, entity_id } = row;
    const user_id = localStorage.getItem("auth_id");
    const endpoint = API_MAP[type]?.removeWarning;
    if (!endpoint) return;
    const body = { note_id, user_id };
    if (type === "lead")     body.lead_id = entity_id;
    if (type === "customer") body.customer_id = entity_id;
    const res = await FetchNodePostData(setLoading, endpoint, null, body);
    if (res?.data?.status) {
      refreshRef.current();
    }
  }, []);

  const onVerifyNote = useCallback(async (row) => {
    const isVerified = row.verification_status == 1;
    const confirm = await Swal.fire({
      title: isVerified ? "Mark as Pending?" : "Mark as Verified?",
      text: isVerified
        ? "This note will be moved back to pending verification."
        : "Please confirm that you have read this note and want to mark it as verified.",
      icon: "question",
      showCancelButton: true,
      confirmButtonText: isVerified ? "Yes, Mark Pending" : "Yes, Verify",
      cancelButtonText: "Cancel",
      confirmButtonColor: "#5c0385",
    });
    if (!confirm.isConfirmed) return;
    const { id: note_id, type, verification_status } = row;
    const newStatus = verification_status == 1 ? 0 : 1;
    const endpoint = API_MAP[type]?.verify;
    if (!endpoint) return;
    const res = await FetchNodePostData(setLoading, endpoint, null, { note_id, verification_status: newStatus });
    if (res?.data?.status) {
      refreshRef.current();
    }
  }, []);

  const columns = useMemo(
    () => [
      {
        Header: "Date",
        accessor: "created_at",
        className: "text-center",
        isShortIcon: true,
        Cell: (cellProps) => {
          const d = new Date(cellProps.row.original.created_at);
          return (
            <div>
              <div className="fw-medium">
                {d.toLocaleDateString("en-IN", {
                  day: "2-digit",
                  month: "2-digit",
                  year: "numeric",
                })}
              </div>
              <small className="text-muted">
                {d.toLocaleTimeString("en-IN", {
                  hour: "2-digit",
                  minute: "2-digit",
                })}
              </small>
            </div>
          );
        },
      },
      {
        Header: "",
        accessor: "type",
        className: "text-center",
        colStyle: { width: "120px", minWidth: "120px" },
        Cell: (cellProps) => {
          const { type, added_by, addedby_avatar } = cellProps.row.original;
          const typeConfig = {
            lead:     { icon: "bi-bullseye",          bg: "#4a6cf7", label: "Lead"     },
            customer: { icon: "bi-person-check-fill", bg: "#0dcaf0", label: "Customer" },
            project:  { icon: "bi-kanban-fill",       bg: "#7c3aed", label: "Project"  },
          };
          const cfg = typeConfig[type] || { icon: "bi-question-circle", bg: "#6c757d", label: type };
          return (
            <div className="d-flex align-items-center justify-content-center gap-2">
              <span
                title={cfg.label}
                style={{
                  display: "inline-flex", alignItems: "center", justifyContent: "center",
                  width: "30px", height: "30px", borderRadius: "50%",
                  background: cfg.bg, color: "#fff", fontSize: "13px", flexShrink: 0,
                }}
              >
                <i className={`bi ${cfg.icon}`}></i>
              </span>
              {addedby_avatar ? (
                <img
                  src={`${configure.imgUrl}/${addedby_avatar}`}
                  alt={added_by}
                  title={added_by}
                  className="rounded-circle flex-shrink-0"
                  style={{ width: "30px", height: "30px", objectFit: "cover" }}
                  onError={(e) => { e.target.onerror = null; e.target.style.display = "none"; }}
                />
              ) : (
                <div
                  title={added_by}
                  className="rounded-circle bg-primary d-flex align-items-center justify-content-center text-white flex-shrink-0"
                  style={{ width: "30px", height: "30px", fontSize: "11px" }}
                >
                  {added_by?.charAt(0)?.toUpperCase()}
                </div>
              )}
            </div>
          );
        },
      },
      {
        Header: "Entity",
        accessor: "entity_name",
        className: "text-center",
        colStyle: { minWidth: "180px", maxWidth: "220px" },
        Cell: (cellProps) => {
          const { entity_name, entity_id } = cellProps.row.original;
          return (
            <div>
              <div className="fw-medium" style={{ whiteSpace: "normal", wordBreak: "break-word", lineHeight: "1.4" }}>{entity_name || "—"}</div>
              <small className="text-muted">#{entity_id}</small>
            </div>
          );
        },
      },
      {
        Header: "Notes",
        accessor: "description",
        className: "text-left",

        Cell: (cellProps) => {
          const html = cellProps.row.original.description || "—";
          return <NotesCell html={html} onView={setViewNote} />;
        },
      },
      {
        Header: "AI Score",
        accessor: "meanScore",
        className: "text-center",

        isShortIcon: true,
        Cell: (cellProps) => {
          const score = cellProps.row.original.meanScore ?? 0;
          let bgColor = "bg-success";
          if (score < 30) bgColor = "bg-danger";
          else if (score < 50) bgColor = "bg-warning text-dark";

          return (
            <div className="d-flex align-items-center justify-content-center gap-2">
              <span
                className={`badge ${bgColor}`}
                style={{
                  fontSize: "12px",
                  padding: "5px 10px",
                  minWidth: "36px",
                }}
              >
                {score}
              </span>
              <ScoreFlag score={score} />
            </div>
          );
        },
      },
      {
        Header: "AI Warnings",
        accessor: "cumulative_ai_warnings",
        className: "text-center",
        isShortIcon: true,
        Cell: (cellProps) => {
          const row = cellProps.row.original;
          const warnings = row.cumulative_ai_warnings ?? 0;
          const { added_by } = row;
          let badgeColor = "bg-success";
          if (warnings >= 5) badgeColor = "bg-danger";
          else if (warnings >= 2) badgeColor = "bg-warning text-dark";

          return (
            <div className="d-flex flex-column align-items-center gap-1">
              <span
                className={`badge ${badgeColor} d-flex align-items-center gap-1`}
                style={{ fontSize: "12px", padding: "5px 10px" }}
                title={`${added_by} has ${warnings} total AI warning${warnings !== 1 ? "s" : ""}`}
              >
                <i className="bi bi-exclamation-triangle-fill" style={{ fontSize: "11px" }}></i>
                {warnings}
              </span>
              <small className="text-muted" style={{ fontSize: "10px" }}>total</small>
              {row.aiWarning == 1 && (
                <button
                  className="btn btn-sm btn-purple mt-1"
                  style={{ fontSize: "11px", padding: "2px 6px" }}
                  title="Reset AI warning to 0"
                  onClick={() => onRemoveWarning(row)}
                >
                  <i className="bi bi-x-circle"></i>
                </button>
              )}
            </div>
          );
        },
      },
      {
        Header: "Verified",
        accessor: "verification_status",
        className: "text-center",
        Cell: (cellProps) => {
          const row = cellProps.row.original;
          const isVerified = row.verification_status == 1;
          return (
            <div className="d-flex flex-column align-items-center gap-1">
              <span
                className={`badge ${isVerified ? "bg-success" : "bg-secondary"} d-flex align-items-center gap-1`}
                style={{ fontSize: "11px", padding: "4px 8px" }}
              >
                <i className={`bi ${isVerified ? "bi-shield-check" : "bi-shield-x"}`}></i>
                {isVerified ? "Verified" : "Pending"}
              </span>
              {!isVerified && (
                <button
                  className="btn btn-sm btn-purple mt-1"
                  style={{ fontSize: "11px", padding: "2px 6px" }}
                  title="Mark as Verified"
                  onClick={() => onVerifyNote(row)}
                >
                  <i className="bi bi-shield-check"></i>
                </button>
              )}
            </div>
          );
        },
      },
    ],
    [onRemoveWarning, onVerifyNote],
  );

  return (
    <React.Fragment>
      {loading && <Loader />}
      <Row>
        <Col lg="12">
          <div className="card">
            {/* Header */}
            <div
              className="card-header border-bottom d-flex align-items-center justify-content-between flex-wrap gap-3 py-3 px-4"
              style={{
                background: "#fff",
                position: "sticky",
                top: -10,
                zIndex: 10,
              }}
            >
              <div>
                <h5 className="text-purple mb-0 fw-bold d-flex align-items-center gap-2 flex-wrap">
                  <i className="bx bx-notepad fs-5"></i>
                  Notes Analysis
                  {verificationStats.total > 0 && (
                    <>
                      <span
                        className="badge"
                        style={{ background: "#e8f5e9", color: "#2e7d32", fontSize: "11px", fontWeight: 500 }}
                        title="Verified notes"
                      >
                        <i className="bi bi-shield-check me-1"></i>
                        {verificationStats.verified} Verified
                      </span>
                      <span
                        className="badge"
                        style={{ background: "#fff3e0", color: "#e65100", fontSize: "11px", fontWeight: 500 }}
                        title="Notes pending verification"
                      >
                        <i className="bi bi-hourglass-split me-1"></i>
                        {verificationStats.pending} Pending
                      </span>
                    </>
                  )}
                </h5>
                <div className="d-flex align-items-center gap-3 mt-2">
                  {[
                    { icon: "bi-bullseye",          bg: "#4a6cf7", label: "Lead"     },
                    { icon: "bi-person-check-fill", bg: "#0dcaf0", label: "Customer" },
                    { icon: "bi-kanban-fill",       bg: "#7c3aed", label: "Project"  },
                  ].map((t) => (
                    <span key={t.label} className="d-flex align-items-center gap-1" style={{ fontSize: "11px", color: "#6c757d" }}>
                      <span style={{ display: "inline-flex", alignItems: "center", justifyContent: "center", width: "18px", height: "18px", borderRadius: "50%", background: t.bg, color: "#fff", fontSize: "10px" }}>
                        <i className={`bi ${t.icon}`}></i>
                      </span>
                      {t.label}
                    </span>
                  ))}
                </div>
              </div>

              <div className="d-flex align-items-center gap-2">
                <span
                  className="text-muted fw-medium"
                  style={{ fontSize: "13px", whiteSpace: "nowrap" }}
                >
                  Date Range :
                </span>
                <RangePicker
                  value={[dayjs(fromDate, "YYYY-MM-DD"), dayjs(toDate, "YYYY-MM-DD")]}
                  format="DD-MM-YYYY"
                  onChange={(dates) => {
                    if (dates) {
                      setFromDate(dates[0].format("YYYY-MM-DD"));
                      setToDate(dates[1].format("YYYY-MM-DD"));
                    }
                  }}
                  allowClear={false}
                  style={{ borderRadius: "6px" }}
                />
              </div>
            </div>

            {/* Table */}
            <div className="responsive-table">
              <TableContainer
                columns={columns}
                data={filteredData}
                personFilter={personFilter}
                setPersonFilter={setPersonFilter}
                personOptions={personOptions}
                typeFilter={typeFilter}
                setTypeFilter={setTypeFilter}
              />
            </div>
          </div>
        </Col>
      </Row>
      <Modal isOpen={!!viewNote} toggle={() => setViewNote(null)} size="lg" scrollable>
        <ModalHeader toggle={() => setViewNote(null)}>Full Notes</ModalHeader>
        <ModalBody>
          <div
            className="notes-html-content"
            style={{ lineHeight: "1.7", whiteSpace: "normal", wordBreak: "break-word" }}
            dangerouslySetInnerHTML={{ __html: viewNote || "" }}
          />
        </ModalBody>
      </Modal>
    </React.Fragment>
  );
};

export default Notesanalysis;
